/**
 * FirmGuidesIndex — `/firm-guides`. Browse index for all 132
 * "How to get into {Firm}" landing pages.
 *
 * The individual pages ship to Google under the SEO-friendly
 * `/how-to-get-into/{slug}` route, but users need an in-app browse
 * surface too — that's this page. Facets by sector (banking /
 * consulting / law / tech / accounting / …) with a free-text
 * search box so a family typing "Deloitte" gets there in one keystroke.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Trophy, Search, X, ArrowRight, Landmark, Target, BookOpen,
  Sparkles, Briefcase,
} from "lucide-react";
import PathwaySEO from "@/components/app/PathwaySEO";
import FirmLogo from "@/components/app/FirmLogo";

const SECTOR_META = {
  banking:    { label: "Banking",     Icon: Landmark },
  consulting: { label: "Consulting",  Icon: Target },
  law:        { label: "Law",         Icon: BookOpen },
  tech:       { label: "Tech",        Icon: Sparkles },
  accounting: { label: "Accounting",  Icon: Briefcase },
};

function labelFor(sector) {
  return SECTOR_META[(sector || "").toLowerCase()]?.label || (sector || "Other");
}

export default function FirmGuidesIndex() {
  const navigate = useNavigate();
  const [firms, setFirms] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [activeSector, setActiveSector] = useState("");
  // Autocomplete state — 651 firms means scrolling is a chore, so
  // typing surfaces the top 8 matches in a floating dropdown with
  // arrow-key + Enter navigation. Grid below still filters in sync.
  const [acOpen, setAcOpen] = useState(false);
  const [acIdx, setAcIdx] = useState(-1);
  const inputRef = useRef(null);
  const acBoxRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/employers/curated")
      .then((r) => !cancelled && setFirms(r.data.items || []))
      .catch(() => !cancelled && setErr("network"));
    return () => { cancelled = true; };
  }, []);

  const { visible, sectorCounts, totalSectors } = useMemo(() => {
    const list = firms || [];
    const qn = q.trim().toLowerCase();
    const counts = {};
    for (const f of list) {
      const s = (f.sector || "other").toLowerCase();
      counts[s] = (counts[s] || 0) + 1;
    }
    const vis = list.filter((f) => {
      if (activeSector && (f.sector || "").toLowerCase() !== activeSector) return false;
      if (qn) {
        const hay = `${f.firm_name || ""} ${f.slug || ""} ${(f.countries || []).join(" ")}`.toLowerCase();
        if (!hay.includes(qn)) return false;
      }
      return true;
    });
    return { visible: vis, sectorCounts: counts, totalSectors: Object.keys(counts).length };
  }, [firms, q, activeSector]);

  // Autocomplete matches — ranks by name-startsWith first, then name-includes.
  // Capped at 8 rows so the dropdown stays scannable at a glance.
  const acMatches = useMemo(() => {
    const qn = q.trim().toLowerCase();
    if (!qn || !firms) return [];
    const startsWith = [];
    const includes = [];
    for (const f of firms) {
      const nm = (f.firm_name || "").toLowerCase();
      if (nm.startsWith(qn)) startsWith.push(f);
      else if (nm.includes(qn) || (f.slug || "").includes(qn)) includes.push(f);
    }
    return [...startsWith, ...includes].slice(0, 8);
  }, [q, firms]);

  // Reset highlighted row whenever the matches shift.
  useEffect(() => { setAcIdx(acMatches.length > 0 ? 0 : -1); }, [acMatches]);

  // Close the dropdown when the user clicks outside the search area.
  useEffect(() => {
    const onDoc = (e) => {
      if (!acBoxRef.current) return;
      if (!acBoxRef.current.contains(e.target)) setAcOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const onAcKey = (e) => {
    if (!acOpen || acMatches.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setAcIdx((i) => (i + 1) % acMatches.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setAcIdx((i) => (i - 1 + acMatches.length) % acMatches.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const pick = acMatches[acIdx] || acMatches[0];
      if (pick) navigate(`/how-to-get-into/${pick.slug}`);
    } else if (e.key === "Escape") {
      setAcOpen(false);
      inputRef.current?.blur();
    }
  };

  if (err) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16">
        <p className="text-stone-500">Couldn't load the firm guides.</p>
      </div>
    );
  }

  return (
    <div data-testid="firm-guides-index" className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
      <PathwaySEO
        title={`How to get into every top graduate employer — ${firms?.length || 651} sourced guides`}
        description="Browse every 'How to get into {Firm}' guide on WhichUniversityAdvisor. Sourced feeder universities, application timelines, academic bars, related firms. No prestige scores — just where graduates actually ended up."
        path="/firm-guides"
      />

      {/* HERO */}
      <div>
        <div className="label-uppercase text-[#4C1D95] flex items-center gap-1.5">
          <Trophy className="h-3 w-3" /> Firm guides
        </div>
        <h1
          data-testid="firm-guides-heading"
          className="mt-2 font-editorial text-4xl sm:text-5xl leading-tight text-[#002147]"
        >
          How to get into every top graduate employer
        </h1>
        <p className="mt-3 text-stone-700 leading-relaxed max-w-3xl">
          {firms?.length || 651} sourced guides, one per firm. Each page
          lists the universities whose graduate destinations data
          actually names that employer, the typical academic bar, and
          the internship route that leads to a full-time offer. Search
          for a specific firm, or browse by sector.
        </p>
      </div>

      {/* SEARCH + SECTOR TABS */}
      <div className="mt-8 space-y-4">
        <div className="relative max-w-md" ref={acBoxRef}>
          <Search className="h-4 w-4 text-stone-400 absolute left-3 top-3 pointer-events-none" />
          <input
            data-testid="firm-guides-search"
            ref={inputRef}
            value={q}
            onChange={(e) => { setQ(e.target.value); setAcOpen(true); }}
            onFocus={() => setAcOpen(true)}
            onKeyDown={onAcKey}
            placeholder={`Search ${firms?.length || 651} firms — 'Goldman Sachs', 'McKinsey'…`}
            role="combobox"
            aria-expanded={acOpen && acMatches.length > 0}
            aria-controls="firm-guides-autocomplete-list"
            aria-autocomplete="list"
            className="w-full rounded-full border border-stone-300 bg-white py-2 pl-10 pr-10 text-sm outline-none focus:border-[#4C1D95]"
          />
          {q && (
            <button
              type="button"
              onClick={() => { setQ(""); setAcOpen(false); inputRef.current?.focus(); }}
              data-testid="firm-guides-search-clear"
              className="absolute right-3 top-3 text-stone-400 hover:text-stone-700"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}

          {/* Autocomplete dropdown */}
          {acOpen && acMatches.length > 0 && (
            <div
              id="firm-guides-autocomplete-list"
              data-testid="firm-guides-autocomplete"
              role="listbox"
              className="absolute z-20 left-0 right-0 mt-1 rounded-lg border border-stone-200 bg-white shadow-lg overflow-hidden"
            >
              {acMatches.map((f, i) => {
                const Icon = SECTOR_META[(f.sector || "").toLowerCase()]?.Icon || Briefcase;
                return (
                  <Link
                    key={f.slug}
                    to={`/how-to-get-into/${f.slug}`}
                    role="option"
                    aria-selected={i === acIdx}
                    data-testid={`firm-guides-ac-option-${i}`}
                    onMouseEnter={() => setAcIdx(i)}
                    onClick={() => setAcOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2 text-sm border-b border-stone-100 last:border-b-0 ${
                      i === acIdx ? "bg-[#F5F0FF]" : "bg-white"
                    }`}
                  >
                    <FirmLogo
                      slug={f.slug}
                      name={f.firm_name}
                      sector={f.sector}
                      IconFallback={Icon}
                      size={24}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-stone-900 truncate">{f.firm_name}</div>
                      <div className="text-[10px] font-underground-sc tracking-wider text-stone-500">
                        {labelFor(f.sector)}
                      </div>
                    </div>
                    <ArrowRight className="h-3.5 w-3.5 text-stone-400 shrink-0" strokeWidth={2.5} />
                  </Link>
                );
              })}
              <div className="px-3 py-1.5 bg-stone-50 text-[10px] font-underground-sc tracking-wider text-stone-500 border-t border-stone-100">
                ↑↓ to move · Enter to open · Esc to close
              </div>
            </div>
          )}
        </div>

        {firms && totalSectors > 1 && (
          <div className="flex flex-wrap gap-2" data-testid="firm-guides-sector-tabs">
            <SectorPill
              slug=""
              label={`All (${firms.length})`}
              active={activeSector === ""}
              onClick={() => setActiveSector("")}
            />
            {Object.entries(sectorCounts)
              .sort(([, a], [, b]) => b - a)
              .map(([s, n]) => (
                <SectorPill
                  key={s}
                  slug={s}
                  label={`${labelFor(s)} (${n})`}
                  active={activeSector === s}
                  onClick={() => setActiveSector(activeSector === s ? "" : s)}
                />
              ))}
          </div>
        )}
      </div>

      {/* GRID */}
      {!firms ? (
        <div className="mt-10 grid sm:grid-cols-2 md:grid-cols-3 gap-3">
          {Array.from({ length: 9 }).map((_, i) => (
            <div key={i} className="h-24 rounded-lg border border-stone-200 bg-stone-50 animate-pulse" />
          ))}
        </div>
      ) : visible.length === 0 ? (
        <div
          data-testid="firm-guides-empty"
          className="mt-10 rounded-md border border-amber-300 bg-amber-50/60 p-6 text-center text-[13px] text-stone-700"
        >
          No firms match that search. Clear the filter or check the sector tabs above.
        </div>
      ) : (
        <div className="mt-8 grid sm:grid-cols-2 md:grid-cols-3 gap-3" data-testid="firm-guides-grid">
          {visible.map((f) => {
            const Icon = SECTOR_META[(f.sector || "").toLowerCase()]?.Icon || Briefcase;
            return (
              <Link
                key={f.slug}
                to={`/how-to-get-into/${f.slug}`}
                data-testid={`firm-guides-card-${f.slug}`}
                className="group rounded-lg border border-stone-200 bg-white p-4 hover:border-[#4C1D95] hover:shadow-sm transition"
              >
                <div className="flex items-start gap-3">
                  <FirmLogo
                    slug={f.slug}
                    name={f.firm_name}
                    sector={f.sector}
                    IconFallback={Icon}
                    size={36}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-[14px] font-semibold text-stone-900 leading-tight truncate">
                      {f.firm_name}
                    </div>
                    <div className="mt-0.5 text-[11px] font-underground-sc tracking-wider text-stone-500">
                      {labelFor(f.sector)}
                    </div>
                    <div className="mt-2 text-[11px] text-stone-600 truncate">
                      {(f.countries || []).slice(0, 3).join(" · ")}
                      {f.countries && f.countries.length > 3 && ` +${f.countries.length - 3}`}
                    </div>
                  </div>
                  <ArrowRight
                    className="h-3.5 w-3.5 text-stone-300 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all shrink-0 mt-1"
                    strokeWidth={2.5}
                  />
                </div>
              </Link>
            );
          })}
        </div>
      )}

      <p className="mt-8 text-[11px] font-mono text-stone-500">
        Definitions:{" "}
        <Link to="/methodology" className="underline text-[#4C1D95]">
          how we source graduate destinations
        </Link>
        .
      </p>
    </div>
  );
}

function SectorPill({ slug, label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`firm-guides-sector-${slug || "all"}`}
      className={`rounded-full border px-3 py-1 text-[11px] font-underground-sc tracking-wider transition ${
        active
          ? "bg-[#4C1D95] text-white border-[#4C1D95]"
          : "bg-white text-stone-700 border-stone-300 hover:border-stone-500"
      }`}
    >
      {label}
    </button>
  );
}
